/**
 * How a payee name was made (TMMAPP-0163 item 4, TMMAPI-0193 U3): `AI` is the shared `AiPill` (with
 * its confidence in words when one is served), and the free sources are the neutral grey pill with
 * their label: **Your rule**, **Known merchant**, **From the bank text**. Each carries a one-sentence
 * help tooltip, on a focusable wrapper so the keyboard reaches it too. The caller passes the served
 * source only; an absent or unknown one draws nothing.
 */
import { Box, Tooltip } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { NamingSource } from '@/api/namingSource';
import { AiPill } from '@/components/AiPill';
import { Pill } from '@/components/Pill';

const LABEL_KEYS = {
  RULE: 'naming.source.RULE',
  KNOWN_MERCHANT: 'naming.source.KNOWN_MERCHANT',
  BANK_TEXT: 'naming.source.BANK_TEXT',
} as const satisfies Record<Exclude<NamingSource, 'AI'>, string>;

const HELP_KEYS = {
  RULE: 'naming.source.help.RULE',
  KNOWN_MERCHANT: 'naming.source.help.KNOWN_MERCHANT',
  BANK_TEXT: 'naming.source.help.BANK_TEXT',
  AI: 'naming.source.help.AI',
} as const satisfies Record<NamingSource, string>;

export interface NamingSourcePillProps {
  source: NamingSource;
  /** An AI proposal's confidence (0–1), in words on the AI pill. */
  confidence?: number | null | undefined;
}

export function NamingSourcePill({ source, confidence = null }: NamingSourcePillProps) {
  const { t } = useTranslation();
  return (
    <Tooltip title={t(HELP_KEYS[source])} describeChild>
      <Box
        component="span"
        tabIndex={0}
        data-naming-source={source}
        sx={{ display: 'inline-flex', borderRadius: '11px' }}
      >
        {source === 'AI' ? (
          <AiPill confidence={confidence} />
        ) : (
          <Pill wash="grey" data-paint="naming-source">
            {t(LABEL_KEYS[source])}
          </Pill>
        )}
      </Box>
    </Tooltip>
  );
}

/**
 * "Named by: {label}" on a named line (item 4), adding ", confirmed by you" when a person confirmed
 * a name that isn't their own rule's.
 */
export function NamedBy({
  source,
  confirmedByPerson,
}: {
  source: NamingSource;
  confirmedByPerson: boolean;
}) {
  return (
    <Box
      component="span"
      data-named-by=""
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
    >
      <Trans
        i18nKey={
          confirmedByPerson && source !== 'RULE'
            ? 'naming.source.namedByConfirmed'
            : 'naming.source.namedBy'
        }
        components={{ pill: <NamingSourcePill source={source} /> }}
      />
    </Box>
  );
}
